import React, { useEffect, useState, useMemo } from 'react';
import { 
  Container, Grid, Card, CardContent, Typography, Rating, Stack, Box, Button, CircularProgress 
} from '@mui/material';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import groupImage from './img/Group 348464.png';

// 새 활성 사용자 차트 컴포넌트 (NewActiveUsersChart)
const NewActiveUsersChart = ({ data }) => {
  const [chartData, setChartData] = useState([]);

  // data가 변경되면 데이터를 변환 (전체 신규, 활성 신규, 활성 비율 포함)
  useEffect(() => {
    if (data) {
      const formattedData = data.map(item => ({
        date: item.date,
        rate: item.totalNewUsers > 0 ? ((item.activeNewUsers / item.totalNewUsers) * 100).toFixed(2) : 0,
        total: item.totalNewUsers,
        active: item.activeNewUsers,
      }));
      setChartData(formattedData);
    }
  }, [data]);

  // 커스텀 툴팁: 마우스 오버 시 상세 정보 표시
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const { total, active: activeCount, rate } = payload[0].payload;
      return (
        <div style={{ backgroundColor: 'white', border: '1px solid #ccc', padding: '10px' }}>
          <p>{`날짜: ${label}`}</p>
          <p>{`전체 신규 유저: ${total}`}</p>
          <p>{`활성 신규 유저: ${activeCount}`}</p>
          <p>{`활성 비율: ${rate}%`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey="date"
          tickFormatter={(value) => {
            const d = new Date(value);
            return `${d.getMonth() + 1}/${d.getDate()}`;
          }}
        />
        <YAxis domain={[0, 100]} tickFormatter={(value) => `${value}%`} />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Line 
          type="monotone" 
          dataKey="rate" 
          stroke="#8884d8" 
          strokeWidth={2}
          dot={{ r: 3 }}
          name="활성 비율 (%)"
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// 날짜 형식 변환 함수 (예: "2025.04.10")
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

// 간단한 캐시를 위한 Map
const cache = new Map();
const base_uri = '/api';

const DashboardPage = () => {
  const [metrics, setMetrics] = useState({
    totalUsers: null,
    dau: null,
    mau: null,
    retention: null,
  });
  const [installData, setInstallData] = useState({
    cumulative_difference: null,
    cumulative_installs: null,
  });
  const [reviewData, setReviewData] = useState(null);
  const [responseRateData, setResponseRateData] = useState(null);
  const [newActiveUsersData, setNewActiveUsersData] = useState(null);
  const [error, setError] = useState(null);

  // fetchWithCache 함수: 동일 URL 호출 시 캐시된 데이터를 사용
  const fetchWithCache = async (url) => {
    const cacheKey = url;
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    cache.set(cacheKey, data);
    return data;
  };

  useEffect(() => {
    let isMounted = true;

    const fetchTotalUsers = async () => {
      try {
        const totalUsersData = await fetchWithCache(`${base_uri}/metrics/total-users`);
        if (isMounted) {
          setMetrics(prev => ({ ...prev, totalUsers: totalUsersData.total_users }));
        }
      } catch (err) {
        if (isMounted) {
          console.error("Error fetching total users:", err);
          setError(err.message);
        }
      }
    };

    const fetchDau = async () => {
      try {
        const dauData = await fetchWithCache(`${base_uri}/metrics/dau`);
        if (isMounted) {
          setMetrics(prev => ({ ...prev, dau: dauData }));
        }
      } catch (err) {
        if (isMounted) {
          console.error("Error fetching DAU:", err);
          setError(err.message);
        }
      }
    };

    const fetchMau = async () => {
      try {
        const mauData = await fetchWithCache(`${base_uri}/metrics/mau`);
        if (isMounted) {
          setMetrics(prev => ({ ...prev, mau: mauData }));
        }
      } catch (err) {
        if (isMounted) {
          console.error("Error fetching MAU:", err);
          setError(err.message);
        }
      }
    };

    const fetchRetention = async () => {
      try {
        const retentionData = await fetchWithCache(`${base_uri}/metrics/retention`);
        if (isMounted) {
          setMetrics(prev => ({ ...prev, retention: retentionData }));
        }
      } catch (err) {
        if (isMounted) {
          console.error("Error fetching retention:", err);
          setError(err.message);
        }
      }
    };

    const fetchReviews = async () => {
      try {
        const reviewsData = await fetchWithCache(`${base_uri}/metrics/reviews`);
        if (isMounted) {
          const reviewsArray = Array.isArray(reviewsData) ? reviewsData : reviewsData.all_reviews || [];
          const averageRating = reviewsArray.reduce((sum, review) => sum + review['Star Rating'], 0) / (reviewsArray.length || 1);
          const ratingsDetail = reviewsArray.reduce((acc, review) => {
            const rating = review['Star Rating'];
            acc[rating] = acc[rating] ? { count: acc[rating].count + 1 } : { count: 1 };
            return acc;
          }, {});
          setReviewData({
            source: reviewsData[0]?.source || '없음',
            average_rating: averageRating.toFixed(1),
            ratings_detail: ratingsDetail,
            all_reviews: reviewsArray
          });
        }
      } catch (err) {
        if (isMounted) {
          console.error("Error fetching reviews:", err);
          setError(err.message);
        }
      }
    };

    const fetchInstallData = async () => {
      try {
        const installJson = await fetchWithCache(`${base_uri}/metrics/install-data`);
        if (isMounted) {
          setInstallData(installJson);
        }
      } catch (err) {
        if (isMounted) {
          console.error("Error fetching install data:", err);
          setError(err.message);
        }
      }
    };

    const fetchResponseRate = async () => {
      try {
        const responseRateJson = await fetchWithCache(`${base_uri}/metrics/response-rate`);
        if (isMounted) {
          setResponseRateData(responseRateJson);
        }
      } catch (err) {
        if (isMounted) {
          console.error("Error fetching response rate data:", err);
          setError(err.message);
        }
      }
    };

    const fetchNewActiveUsers = async () => {
      try {
        const newActiveUsers = await fetchWithCache(`${base_uri}/metrics/new-active-users`);
        if (isMounted) {
          setNewActiveUsersData(newActiveUsers);
        }
      } catch (err) {
        if (isMounted) {
          console.error("Error fetching new active users:", err);
          setError(err.message);
        }
      }
    };

    // 각 API 호출 함수 실행
    fetchTotalUsers();
    fetchDau();
    fetchMau();
    fetchRetention();
    fetchReviews();
    fetchInstallData();
    fetchResponseRate();
    fetchNewActiveUsers();

    return () => {
      isMounted = false;
    };
  }, []);

  // 7일, 14일 리텐션 데이터를 하나의 배열로 결합
  const combinedRetentionData = useMemo(() => {
    if (
      !metrics.retention ||
      !metrics.retention["7_days_ago"] ||
      !metrics.retention["14_days_ago"]
    ) {
      return [];
    }
    const sevenRates = metrics.retention["7_days_ago"].retention_rates;
    const fourteenRates = metrics.retention["14_days_ago"].retention_rates;
    return sevenRates.map((item) => {
      const fourteenItem = fourteenRates.find(r => r.day === item.day);
      return {
        day: item.day,
        retention_7_days: item.rate,
        retention_14_days: fourteenItem ? fourteenItem.rate : 0
      };
    });
  }, [metrics.retention]);

  // 별점 데이터를 차트용 데이터로 변환
  const ratingChartData = useMemo(() => {
    if (!reviewData || !reviewData.ratings_detail) return [];
    return Object.entries(reviewData.ratings_detail).map(([rating, data]) => ({
      rating: Number(rating),
      count: data.count
    }));
  }, [reviewData]);

  // 응답율 데이터 가공
  const formattedResponseRateData = useMemo(() => {
    if (!responseRateData) return [];
    return responseRateData.map(item => ({
      ...item,
      formattedDate: new Date(item.date).toLocaleDateString('ko-KR', {
        month: 'numeric',
        day: 'numeric'
      })
    }));
  }, [responseRateData]);

  if (error) {
    return <Typography color="error">Error: {error}</Typography>;
  }

  return (
    <Container>
      <Box display="flex" justifyContent="center" alignItems="center" mb={3}>
        <img src={groupImage} alt="Group 348464" style={{ maxWidth: '100%', height: 'auto' }} />
      </Box>

      {/* 상단 버튼 영역 */}
      <Grid container spacing={3} direction="column" mb={3}>
        <Grid item xs={12} md={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                sx={{ backgroundColor: '#2E7D32', color: 'white', width: '100%' }}
                onClick={() => window.open('https://platform.openai.com/settings/organization/billing/overview', '_blank')}
              >
                GPT 잔액 확인하기
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                sx={{ backgroundColor: '#1976D2', color: 'white', width: '100%' }}
                onClick={() => window.open('https://app.amplitude.com/analytics/kiyeom/home', '_blank')}
              >
                Amplitude 유저 확인하기
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Total Users */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Total Users</Typography>
              {metrics.totalUsers !== null ? (
                <Typography variant="h4">{metrics.totalUsers}</Typography>
              ) : (
                <CircularProgress />
              )}
            </CardContent>
          </Card>
        </Grid>
        {/* Current MAU */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Current MAU</Typography>
              {metrics.mau ? (
                <Typography variant="h4">{metrics.mau.current_mau}</Typography>
              ) : (
                <CircularProgress />
              )}
            </CardContent>
          </Card>
        </Grid>
        {/* Previous MAU */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">Previous MAU</Typography>
              {metrics.mau ? (
                <Typography variant="h4">{metrics.mau.previous_mau}</Typography>
              ) : (
                <CircularProgress />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* New Active Users 차트 */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6">New Active Users (%)</Typography>
              {newActiveUsersData ? (
                <NewActiveUsersChart data={newActiveUsersData} />
              ) : (
                <CircularProgress />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 통합된 DAU 차트 (전체, 10개 이상 대화, 일기 활동 사용자 수 모두 포함) */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6">Daily Active Users (DAU)</Typography>
              {metrics.dau ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={metrics.dau}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      stroke="#8884d8" 
                      name="전체 일일 사용자 수" 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="realCount" 
                      stroke="#82ca9d" 
                      name="10개 이상 대화 사용자 수" 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="diaryCount" 
                      stroke="#ff7300" 
                      name="일기 활동 사용자 수" 
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <CircularProgress />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* First Day Users (Retention) */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6">First Day Users (Retention)</Typography>
              {metrics.retention ? (
                <>
                  <Typography variant="body1">
                    7 Days Ago: {metrics.retention["7_days_ago"]?.first_day_users || 0}
                  </Typography>
                  <Typography variant="body1">
                    14 Days Ago: {metrics.retention["14_days_ago"]?.first_day_users || 0}
                  </Typography>
                </>
              ) : (
                <CircularProgress />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 리텐션 차트 */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6">Retention Rates (Next 7 Days)</Typography>
              {combinedRetentionData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={combinedRetentionData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="day" 
                      label={{ value: 'Day', position: 'insideBottomRight', offset: 0 }} 
                    />
                    <YAxis label={{ value: 'Rate (%)', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="retention_7_days" 
                      stroke="#ff7300" 
                      name="7 Days Ago" 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="retention_14_days" 
                      stroke="#8884d8" 
                      name="14 Days Ago" 
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <CircularProgress />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 응답률 차트 */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6">Daily Push Response Rate</Typography>
              {responseRateData ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={formattedResponseRateData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="formattedDate" 
                      tick={{ fontSize: 12 }}
                      tickCount={10}
                      label={{ value: '날짜', position: 'insideBottomRight', offset: 0 }}
                    />
                    <YAxis 
                      domain={[0, 100]} 
                      label={{ value: '응답률 (%)', angle: -90, position: 'insideLeft' }} 
                    />
                    <Tooltip 
                      formatter={(value) => [`${value}%`, '응답률']}
                      labelFormatter={(label) => `날짜: ${label}`}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="rate" 
                      stroke="#FF5722" 
                      name="응답률" 
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <CircularProgress />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 누적 유저 차이 차트 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6">Cumulative User Difference</Typography>
              {installData.cumulative_difference ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={installData.cumulative_difference}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis label={{ value: 'Difference', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="value" stroke="#82ca9d" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <CircularProgress />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 누적 유저 설치 차트 */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6">Cumulative User Installs</Typography>
              {installData.cumulative_installs ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={installData.cumulative_installs}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis label={{ value: 'Installs', angle: -90, position: 'insideLeft' }} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="value" stroke="#8884d8" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                  <CircularProgress />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* 최근 리뷰 섹션 */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ marginBottom: 2 }}>
                Recent Reviews
              </Typography>
              {reviewData ? (
                <Stack spacing={2}>
                  {reviewData.all_reviews.slice(0, 5).map((review, index) => (
                    <Card key={index} variant="outlined">
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Rating value={review['Star Rating']} readOnly />
                          <Typography variant="body2" color="textSecondary">
                            {formatDate(review['Review Date'])} ({review.source})
                          </Typography>
                        </Box>
                        <Typography variant="body1">
                          {review['Review Text']}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <CircularProgress />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DashboardPage;
